import { useState } from "react";
import { ChevronDown, Fuel, SlidersHorizontal } from "lucide-react";
import clsx from "clsx";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
} from "recharts";

const RemainingFuelOnboard = ({ remainingFuel }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [selectedTypes, setSelectedTypes] = useState([]);

  // Color palette
  const colors = ["#1a5276", "#27ae60", "#d35400", "#7d3c98"];

  // Initialize selected types when remainingFuel data is available
  useState(() => {
    if (remainingFuel && remainingFuel.length > 0) {
      setSelectedTypes(remainingFuel.map(item => item.name));
    }
  }, [remainingFuel]);

  const handleToggle = (type) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  // Filter data based on selected types
  const filteredData = (remainingFuel ?? [])
    .filter(item => selectedTypes.includes(item.name))
    .map((item, index) => ({
      ...item,
      tooltipName: item.name,
      color: colors[index % colors.length],
    }));

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;

    const data = payload[0].payload;
    const value = payload[0].value;
    const valueColor = data.color;

    return (
      <div
        style={{
          backgroundColor: "#ffffff",
          borderRadius: "8px",
          border: "1px solid #e2e8f0",
          boxShadow: "rgba(0, 0, 0, 0.12) 0px 4px 12px",
          padding: "10px 14px",
          fontSize: "13px",
        }}
      >
        <div style={{ color: "#1e293b", fontWeight: 600, marginBottom: 4 }}>
          {data.tooltipName}
        </div>
        <div style={{ color: valueColor, fontWeight: 600 }}>{value} MT</div>
      </div>
    );
  };

  return (
    <div className="w-full rounded-xl bg-white shadow-lg ring-1 ring-blue-100/30 transition-all duration-300 overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="bg-gradient-to-br from-[#1a325d] to-[#12213f] w-full flex items-center justify-between px-4 py-3 text-left"
      >
        <div className="flex items-center gap-3">
          <Fuel size={20} strokeWidth={2} className="text-blue-400" />
          <h3 className="text-[15px] font-semibold text-white tracking-wide">
            Remaining Fuel Onboard
          </h3>
        </div>
        <ChevronDown
          className={clsx(
            "w-5 h-5 text-white transition-transform duration-300",
            isOpen && "rotate-180"
          )}
        />
      </button>

      {/* Filter Section */}
      {remainingFuel && remainingFuel.length > 0 && (
        <div className="px-4 py-3">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <SlidersHorizontal className="w-4 h-4 text-blue-600" />
              Select by:
            </label>
            <div className="flex flex-wrap gap-2">
              {remainingFuel.map((item, index) => {
                const isActive = selectedTypes.includes(item.name);
                const color = colors[index % colors.length];

                return (
                  <button
                    key={item.name}
                    onClick={() => handleToggle(item.name)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-200 border flex items-center gap-2
                      ${isActive
                        ? "text-white shadow-sm border-transparent"
                        : "text-gray-600 bg-white border-gray-300 hover:bg-gray-50"
                      }`}
                    style={{
                      backgroundColor: isActive ? color : undefined,
                    }}
                  >
                    {item.name}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Chart */}
      <div
        className={clsx(
          "transition-all duration-500 overflow-hidden px-4 bg-white",
          isOpen ? "py-4 max-h-[500px]" : "py-0 max-h-0"
        )}
      >
        {filteredData.length > 0 ? (
          <ResponsiveContainer width="100%" height={425}>
            <BarChart
              data={filteredData}
              margin={{ top: 10, right: 10, left: 10, bottom: 10 }}
            >
              <XAxis
                dataKey="name"
                type="category"
                tick={{ fontSize: 12 }}
                interval={0}
                height={40}
              />
              <YAxis
                type="number"
                tick={{ fontSize: 12 }}
                axisLine={false}
                tickLine={false}
                label={{
                  value: "Remaining Fuel MT",
                  angle: -90,                
                  position: "insideLeft",
                  style: { textAnchor: "middle", fontSize: "0.8em", fill: "rgb(102, 102, 102)" }
                }}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: "#f1f5f9" }} />

              <Bar dataKey="value" radius={[6, 6, 0, 0]} barSize={30}>
                {filteredData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-center py-8 text-gray-500">
            No fuel types selected
          </div>
        )}
      </div>
    </div>
  );
};

export default RemainingFuelOnboard;